/* eslint-disable react-hooks/exhaustive-deps */

import React, {useMemo, useState} from 'react';
import {useQuery, useQueryClient, useMutation} from 'react-query';
import * as flowQuery from '../../../../../requests/queries/flows';
import {toast} from 'react-toastify';
import {defaultFlowProcess} from '../defaultProcess';
import {v4} from 'uuid';
import {CreditCheckContext} from '../../../../../helpers/context/CreditCheckContext';
import {fetchAvailableFields} from '../../../../../requests/queries/flows';
import {useSelector} from 'react-redux';
import {mergeRules} from '../utils';
import {transformRulesData} from '../../UpdatedEditProcess/hooks/ruleUtils';
import {useParams} from 'react-router-dom';

export const useFlowData = () => {
  const {slug} = useParams();
  const {user} = useSelector(state => state?.auth);
  const [rules, setRules] = useState([]);
  const queryClient = useQueryClient();
  const {
    set: {setTempFlow},
    processArrayWithTypes,
  } = React.useContext(CreditCheckContext);

  const fetchTasks = async () => {
    const [stagedResponse, publishedResponse] = await Promise.all([
      flowQuery.fetchTasks(slug),
      flowQuery.fetchPublishedTasks(slug),
    ]);

    return {
      data: {
        changes: stagedResponse?.data?.changes || [],
      },
      rules: publishedResponse?.rules || [],
    };
  };

  const {
    data: allTasks,
    isLoading: gettingTasks,
    isFetching,
  } = useQuery(['all-tasks', slug], fetchTasks, {
    // placeholderData: flowProcess?.process,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const {data: fieldsType, isLoading} = useQuery(
    'fetchAvailableFields',
    fetchAvailableFields,
  );

  const fieldsAndOperators = useMemo(
    () => !isLoading && fieldsType,
    [fieldsType, isLoading],
  );

  const {mutateAsync: addNewTask, isLoading: addingTask} = useMutation(
    task => flowQuery.addTask(slug, task),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('all-tasks');
      },
      onError: error => toast.error(error?.response?.data?.error),
    },
  );

  const {mutateAsync: updateExistingTask, isLoading: updatingTask} =
    useMutation(task => flowQuery.updateTask(slug, task), {
      onSuccess: () => {
        queryClient.invalidateQueries('all-tasks');
      },
      onError: error => toast.error(error?.response?.data?.error),
    });

  const {mutateAsync: saveFlowProcess, isLoading: savingProcess} = useMutation(
    process => flowQuery.saveProcess(slug, process),
    {
      onSuccess: () => toast.success('Process saved successfully'),
      onError: error => toast.error(error.response.data.error),
    },
  );
  const {
    mutateAsync: saveRuleListsAssociation,
    isLoading: isSavingListAssociation,
    isFetching: isFetchingListAssociation,
  } = useMutation(ruleList => flowQuery.createRuleListAssociation(ruleList), {
    onError: error => toast.error(error.response.data.error),
  });
  const {mutateAsync: clearRule, isLoading: clearingRule} = useMutation(
    () => flowQuery.clearProjectRule(slug),
    {
      onSuccess: () => {
        toast.success('Rule cleared successfully from flow');
        queryClient.invalidateQueries('all-tasks');
      },
      onError: error => toast.error(error.response.data.error),
    },
  );
  const {mutateAsync: deleteRule, isLoading: deletingRule} = useMutation(
    id => flowQuery.deleteTask({id, userId: user.id}),
    {
      onSuccess: () => {
        toast.success('Rule deleted successfully');
        queryClient.invalidateQueries('all-tasks');
      },
      onError: error => toast.error(error.response.data.error),
    },
  );
  const {mutateAsync: publishRules, isLoading: publishingRules} = useMutation(
    () => flowQuery.publishTasks(slug, user.id),
    {
      onSuccess: () => {
        toast.success('Flow with saved rules published successfully');
        queryClient.invalidateQueries('all-tasks');
        queryClient.invalidateQueries('pr-company-details');
        localStorage.removeItem('isModified');
      },
      onError: error => toast.error(error.response.data.error),
    },
  );

  React.useEffect(() => {
    if (!gettingTasks) {
      const mergedRules = mergeRules(allTasks, {rules: allTasks?.rules || []});
      localStorage.setItem(
        'isModified',
        JSON.stringify(allTasks?.data?.changes?.length > 0),
      );
      const flowArray =
        mergedRules.length > 0 ? mergedRules : allTasks?.rules || [];

      const start = defaultFlowProcess?.process[0];
      const end = defaultFlowProcess?.process[2];

      const newTempFlow = flowArray.map(({...rule}) => {
        const currentConditions =
          rule?.conditions || rule?.rule?.conditions || {};
        const currentEvent = rule?.event || rule?.rule?.event || {};
        const conditionKey = Object.keys(currentConditions)[0];

        return {
          key: v4(),
          ...(rule.rule || rule),
          id: rule?.rule?.id || rule?.id || '',
          risk_score: currentEvent?.params?.risk_score,
          currentRuleCondition: conditionKey,
          configurations: conditionKey ? currentConditions[conditionKey] : [],
        };
      });
      setRules(transformRulesData(newTempFlow));
      const FLOW_PROCESS = [start, newTempFlow, end];
      setTempFlow(FLOW_PROCESS);
      // createFlowProcess({process: FLOW_PROCESS});
    }
  }, [
    allTasks?.data?.changes,
    allTasks?.rules,
    // createFlowProcess,
    gettingTasks,
    processArrayWithTypes,
    setTempFlow,
  ]);

  return {
    rules,
    setRules,
    allTasks,
    clearRule,
    deleteRule,
    deletingRule,
    publishRules,
    gettingTasks,
    isFetching,
    addNewTask,
    updateExistingTask,
    saveFlowProcess,
    addingTask,
    updatingTask,
    publishingRules,
    clearingRule,
    savingProcess,
    fieldsAndOperators,
    saveRuleListsAssociation,
    isSavingListAssociation,
    isFetchingListAssociation,
  };
};
